<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { useFloatHubStore } from '/src/stores/floatHub'
import { useChatStore } from '/src/stores/chat'
import { useKnowledgeFloatStore } from '/src/modules/knowledge/stores/knowledgeFloat'
import HubBall from './HubBall.vue'
import SubBubble from './SubBubble.vue'
import ChatPanel from '/src/components/chat/ChatPanel.vue'
import KnowledgeFloatPanel from '/src/modules/knowledge/components/KnowledgeFloatPanel.vue'

const hubStore = useFloatHubStore()
const chatStore = useChatStore()
const kbFloatStore = useKnowledgeFloatStore()

// 子气泡配置 - 后续新增功能只需在此数组添加
const modules = [
  { id: 'chat', icon: '💬', label: '会话', color: '#0ea5e9' },
  { id: 'knowledge', icon: '📚', label: '知识库', color: '#0284c7' }
]

// 以中心球为圆心，均匀环绕排列在上半圆
const subBubbles = computed(() => {
  const count = modules.length
  // 在上半圆均匀分布：从 -180°(左) 到 0°(右)，等分
  const arcSpan = 180 // 上半圆 180°
  const gap = arcSpan / (count + 1) // 间隔角度
  return modules.map((mod, i) => ({
    ...mod,
    angle: -180 + gap * (i + 1) // 从左上到右上均匀分布
  }))
})

const handleSubBubbleClick = (moduleId) => {
  if (moduleId === 'chat') {
    // 复用原有 chat store 逻辑
    chatStore.isExpanded = true
    if (!chatStore.panelPosition.x) {
      chatStore.panelPosition = {
        x: hubStore.hubPosition.x - chatStore.panelSize.width / 2 + 24,
        y: hubStore.hubPosition.y - chatStore.panelSize.height - 20
      }
    }
  } else if (moduleId === 'knowledge') {
    kbFloatStore.openPanel()
    if (!kbFloatStore.panelPosition.x) {
      kbFloatStore.setPanelPosition({
        x: hubStore.hubPosition.x - kbFloatStore.panelSize.width / 2 + 24,
        y: hubStore.hubPosition.y - kbFloatStore.panelSize.height - 20
      })
    }
  }
  hubStore.openModule(moduleId)
}

// Ctrl+Shift+C 打开会话面板
const handleGlobalKeydown = (e) => {
  if (e.ctrlKey && e.shiftKey && e.key === 'C') {
    e.preventDefault()
    if (hubStore.activeModule === 'chat') {
      chatStore.isExpanded = false
      hubStore.closeModule()
    } else {
      handleSubBubbleClick('chat')
    }
  }
}

// 点击外部关闭子气泡
const handleClickOutside = (e) => {
  if (hubStore.isOpen) {
    // 检查是否点击在 hub 区域外
    const hubEl = document.querySelector('.hub-ball')
    const subEls = document.querySelectorAll('.sub-bubble')
    let isInside = hubEl?.contains(e.target)
    subEls.forEach(el => {
      if (el.contains(e.target)) isInside = true
    })
    if (!isInside) {
      hubStore.isOpen = false
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
  document.addEventListener('click', handleClickOutside, true)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
  document.removeEventListener('click', handleClickOutside, true)
})

const getBadge = (moduleId) => {
  if (moduleId === 'chat') return chatStore.unreadCount
  return 0
}
</script>

<template>
  <div class="float-hub">
    <!-- 子气泡层（定位相对于 hub ball） -->
    <div
      class="sub-bubbles-container"
      :style="{
        left: `${hubStore.hubPosition.x + 24}px`,
        top: `${hubStore.hubPosition.y + 24}px`
      }"
    >
      <SubBubble
        v-for="(bubble, index) in subBubbles"
        :key="bubble.id"
        :icon="bubble.icon"
        :label="bubble.label"
        :color="bubble.color"
        :angle="bubble.angle"
        :radius="70"
        :is-open="hubStore.isOpen"
        :delay="index * 50"
        :badge="getBadge(bubble.id)"
        @click="handleSubBubbleClick(bubble.id)"
      />
    </div>

    <!-- 背景遮罩（子气泡展开时） -->
    <Transition name="overlay-fade">
      <div v-if="hubStore.isOpen" class="hub-overlay" @click="hubStore.isOpen = false" />
    </Transition>

    <!-- 中心气泡 -->
    <HubBall />

    <!-- 会话面板 -->
    <Transition name="panel-slide">
      <ChatPanel v-if="hubStore.activeModule === 'chat' && chatStore.isExpanded" />
    </Transition>

    <!-- 知识库面板 -->
    <Transition name="panel-slide">
      <KnowledgeFloatPanel v-if="hubStore.activeModule === 'knowledge' && kbFloatStore.isExpanded" />
    </Transition>
  </div>
</template>

<style scoped>
.float-hub {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
}

.float-hub > * {
  pointer-events: auto;
}

.sub-bubbles-container {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
}

.sub-bubbles-container > * {
  pointer-events: auto;
}

.hub-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(1px);
  z-index: 9998;
  pointer-events: auto;
}

/* 动画 */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}
</style>
